/**
 * StudentDirectory - browse the students on the platform
 * ------------------------------------------------------
 * Sorted by rating, so students who do good work turn up first. This is the
 * "reviews shown on the marketplace" part of the plan.
 */

import { useEffect, useState, useCallback } from 'react';
import { Search, Users } from 'lucide-react';

import { studentApi } from '../services/api';
import ProfileCard from '../components/ProfileCard';
import ModeratePerson from '../components/ModeratePerson';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import BackLink from '../components/BackLink';
import { useI18n } from '../hooks/useI18n';

export default function StudentDirectory() {
  const { t } = useI18n();
  const [filters, setFilters] = useState({ search: '', schoolYear: '', minRating: '' });
  // Suspended students are hidden by default, because that is what this
  // list is normally for. A moderator who has just suspended somebody from
  // here needs a way to see them again, or the action cannot be undone.
  const [showSuspended, setShowSuspended] = useState(false);
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await studentApi.browse({
        ...filters,
        include: showSuspended ? 'suspended' : '',
        limit: 24,
      });
      setStudents(data.students);
      setTotal(data.total);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, showSuspended]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const update = (name, value) => setFilters((current) => ({ ...current, [name]: value }));

  return (
    <div className="container section">
      <BackLink to="/dashboard" label={t('settingsPage.backToDashboard')} />

      <div className="page-header">
        <h1>{t('directory.title')}</h1>
        <p>
          {t('directory.subtitle')}
        </p>
      </div>

      <div className="filter-bar">
        <div className="filter-grid">
          {/* See the note by the same class in Marketplace.jsx. */}
          <div className="field field-wide">
            <label htmlFor="student-search">
              <Search size={14} style={{ display: 'inline', verticalAlign: -2 }} /> Search
            </label>
            <input
              id="student-search"
              type="search"
              value={filters.search}
              onChange={(event) => update('search', event.target.value)}
              placeholder={t('directory.searchPlaceholder')}
            />
          </div>

          <div className="field">
            <label htmlFor="schoolYear">{t('directory.schoolYear')}</label>
            <select
              id="schoolYear"
              value={filters.schoolYear}
              onChange={(event) => update('schoolYear', event.target.value)}
            >
              <option value="">{t('directory.anyYear')}</option>
              <option value="S5">S5</option>
              <option value="S6">S6</option>
              <option value="S7">S7</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="minRating">{t('directory.minRating')}</label>
            <select
              id="minRating"
              value={filters.minRating}
              onChange={(event) => update('minRating', event.target.value)}
            >
              <option value="">{t('directory.anyRating')}</option>
              <option value="4">{t('directory.fourUp')}</option>
              <option value="4.5">{t('directory.fourHalfUp')}</option>
              <option value="5">{t('directory.fiveOnly')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* This list is only ever served to a moderator, so the controls for
          acting on somebody belong on it. Before this the only way to
          suspend an account was a button on a REPORT, so an inappropriate
          photograph or name - which nobody reports, because the person it
          bothers is not the one looking at the directory - could only be
          dealt with by reporting it yourself first. */}
      <label
        className="row"
        style={{ gap: 10, cursor: 'pointer', marginBottom: 'var(--space-sm)' }}
      >
        <input
          type="checkbox"
          checked={showSuspended}
          onChange={(event) => setShowSuspended(event.target.checked)}
        />
        <span>{t('directory.studentsIncludeSuspended')}</span>
      </label>

      {error && <div className="alert alert-error">{error}</div>}

      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
        {loading ? t('directory.searching') : t('directory.count', { count: total })}
      </p>

      {loading ? (
        <Loader />
      ) : students.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('directory.emptyTitle')}
          message={t('directory.emptyBody')}
        />
      ) : (
        <div className="grid-cards">
          {students.map((student) => (
            <ProfileCard key={student._id} person={student}>
              <ModeratePerson person={student} onChanged={load} />
            </ProfileCard>
          ))}
        </div>
      )}
    </div>
  );
}
